import { useLocation, useNavigate } from "react-router";
import { ActionButton, BottomCTA, BottomSheet, IconBrick } from "@hamlak/ui";
import type { InsufficientDetails, ModelId, ModelInfo } from "@/api/types";
import { platform } from "@/platform";
import type { StoreState } from "@/features/store/StorePage";
import { firstPurchaseOffer, useStoreProducts } from "@/features/store/useStoreProducts";
import { useStoreRegion } from "@/features/store/web/useStoreRegion";
import { t } from "@/i18n/ko";
import { useConfigStore } from "@/stores/configStore";
import { useUiStore } from "@/stores/uiStore";

type Props = {
  open: boolean;
  details: InsufficientDetails | null;
  /** 턴 부족일 때만: 더 싼 모델로 같은 글을 재전송. 추천 답변 부족이면 없음. */
  onContinueWith?: ((model: ModelId) => void) | undefined;
  onClose: () => void;
};

/** `details.model` 단가가 부족했을 때 balance로 보낼 수 있는 가장 싼 다른 모델. */
export function cheaperModel(
  models: readonly ModelInfo[],
  details: InsufficientDetails,
): ModelInfo | undefined {
  const current = models.find((m) => m.id === details.model);
  const limit = current?.costPerTurn ?? details.required;
  return models
    .filter(
      (m) =>
        m.id !== details.model &&
        m.costPerTurn < limit &&
        details.balance >= m.costPerTurn,
    )
    .sort((a, b) => b.costPerTurn - a.costPerTurn)[0];
}

/**
 * 1.13 부족 시트(F06-06). 헤더 "브릭이 부족해요" + `필요 n · 보유 m` 카드(헤더 보유 pill 없음).
 * 첫 결제 특가 대상이면 카드 아래 "첫 결제는 모든 상품 N% 할인이에요"(749:1486, 상점 배너와 같은 규칙).
 * CTA "브릭 충전하기" → /store(앱 = 스토어 결제, 웹 = 토스 웹 결제). 보조 "{싼 모델}로 계속하기".
 */
export function InsufficientSheet({
  open,
  details,
  onContinueWith,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const setStoreReturnTo = useUiStore((s) => s.setStoreReturnTo);
  const models = useConfigStore((s) => s.settings?.pricing.models ?? []);
  // 특가 여부는 상점 목록(서버가 결제 이력으로 `.first` 를 고름)으로 판단 — 상점과 같은 캐시. 웹은 결제 가능 국가일 때만
  const isWeb = platform.clientKind === "web";
  const region = useStoreRegion(isWeb && open);
  const products = useStoreProducts(open && (!isWeb || region.data?.webPurchaseSupported === true));
  const offer = firstPurchaseOffer((products.data?.items ?? []).filter((i) => i.product.available));
  if (!details) return null;
  const cheaper =
    details.model && onContinueWith ? cheaperModel(models, details) : undefined;
  const goStore = () => {
    // 결제 완료 CTA가 이 채팅방으로 돌아오게 한다(1.15).
    setStoreReturnTo(location.pathname);
    onClose();
    navigate("/store", { state: { storeSource: "insufficient" } satisfies StoreState });
  };
  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      header={{
        title: t("insufficient.title"),
        description: t("insufficient.description"),
      }}
      data-testid="insufficient-sheet"
      footer={
        <BottomCTA
          fixed={false}
          primary={{
            children: t("insufficient.cta"),
            onClick: goStore,
          }}
          secondary={{ children: t("common.close"), onClick: onClose }}
        />
      }
    >
      {/* 시안 F06-06(111:5721): 헤더 보유 pill 없음, 본문·CTA 사이 gap 16 */}
      <div className="chat__sheetBody">
        {/* Figma 402:2825 balance 카드: "필요한 브릭 n" · "보유 브릭 m"(brand) 두 행, 숫자 우측 정렬 + 브릭 아이콘 */}
        <dl className="chat__sheetBalance" data-testid="insufficient-balance">
          <div className="chat__sheetRow">
            <dt>{t("insufficient.required")}</dt>
            <dd>
              <IconBrick size={16} />
              <b>{details.required}</b>
            </dd>
          </div>
          <div className="chat__sheetRow">
            <dt>{t("insufficient.balance")}</dt>
            <dd className="chat__sheetRow--brand">
              <IconBrick size={16} />
              <b>{details.balance}</b>
            </dd>
          </div>
        </dl>
        {offer.active && (
          // 시안 749:1486: 카드 아래 gap 12, t3 brand 가운데. "첫 결제는 " + bold 할인 문구
          <p className="chat__sheetFirst" data-testid="insufficient-first-purchase">
            {t("insufficient.firstPurchase.lead")}
            <b>
              {offer.allRate !== null
                ? t("insufficient.firstPurchase.allRate", { percent: offer.allRate })
                : t("insufficient.firstPurchase.other")}
            </b>
          </p>
        )}
        {cheaper && onContinueWith && (
          <div className="chat__sheetSection">
            <ActionButton
              variant="neutralWeak"
              size="medium"
              block
              onClick={() => {
                onClose();
                onContinueWith(cheaper.id);
              }}
            >
              {t("insufficient.continueWith", { model: cheaper.name })}
            </ActionButton>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
